import React from 'react'
import {useIntl} from 'react-intl'
import {
    Box,
    Button,
    Text,
    VisuallyHidden,
    Link
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Menu, MenuButton, MenuList, MenuItem} from '@chakra-ui/react'
import LocaleText from '../locale-text'
import {CheckIcon, ChevronDownIcon} from '../custom-icons'
import {getPathWithLocale} from '@app/utils/url'
import {buildUrlWithLocale} from '@app/utils/url'

import useMultiSite from '../../hooks/use-multi-site'
import {useLocation} from 'react-router-dom'

import {MENU_COMPONENT_GUIDE} from '@app/constants'

/**
 * DrawerMenuLocaleSelector is a component that provides a dropdown menu
 * for selecting the locale within a drawer menu. It utilizes Chakra UI's
 * Menu components to display a list of supported locales, allowing users
 * to switch the language of the application.
 *
 */
const DrawerMenuLocaleSelector = () => {
    const intl = useIntl()
    const location = useLocation()
    const {site, locale: currentLocale, buildUrl} = useMultiSite()
    const {l10n} = site
    const supportedLocales = l10n?.supportedLocales

    return (
        <Menu placement="top-end" variant="navLocaleMenu">
            <MenuButton
                as={Button}
                textStyle={'bodyBase2'}
                textTransform={'uppercase'}
                whiteSpace={'normal'}
                textDecoration={'none'}
                lineHeight={'1.1'}
                p="16px"
                variant={'link'}
                aria-label={intl.formatMessage(MENU_COMPONENT_GUIDE)}
            >
                <Text as="span">
                    <LocaleText shortCode={intl.locale} />
                </Text>
                <ChevronDownIcon boxSize={3} ml={4} aria-hidden="true" />
            </MenuButton>
            <MenuList>
                <VisuallyHidden>
                    <Text>
                        {intl.formatMessage({
                            defaultMessage: 'Language selector',
                            id: 'locale_selector.title'
                        })}
                    </Text>
                </VisuallyHidden>
                <Box defaultValue={intl.locale} as="ul">
                    {supportedLocales.map((localeObject) => {
                        const locale = localeObject.id
                        const localeAlias = localeObject.alias

                        return (
                            <Box key={localeAlias} as="li" textAlign="center" listStyleType="none">
                                <MenuItem
                                    as={Link}
                                    href={buildUrlWithLocale(
                                        location,
                                        site,
                                        currentLocale,
                                        localeObject
                                    )}
                                    onClick={() => {
                                        const newUrl = getPathWithLocale(locale, buildUrl, {
                                            disallowParams: ['refine']
                                        })
                                        window.location = newUrl
                                    }}
                                    textTransform={'uppercase'}
                                    icon={null}
                                >
                                    <Text lang={locale}>
                                        <LocaleText shortCode={locale} />
                                    </Text>

                                    {intl.locale === locale && (
                                        <>
                                            <VisuallyHidden>
                                                {intl.formatMessage({
                                                    defaultMessage: 'Selected',
                                                    id: 'drawer_menu.locale_selector.selected_language'
                                                })}
                                            </VisuallyHidden>
                                            <CheckIcon
                                                boxSize={4}
                                                position="absolute"
                                                top="50%"
                                                transform="translateY(-50%)"
                                                right="10px"
                                                aria-hidden="true"
                                            />
                                        </>
                                    )}
                                </MenuItem>
                            </Box>
                        )
                    })}
                </Box>
            </MenuList>
        </Menu>
    )
}

export default DrawerMenuLocaleSelector
